import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useRouter, usePathname } from "next/navigation";
import { Filters, ClassType, AgeBand } from "@/types/fitness-class";
import { InstructorType } from "@/types/instructor";

const DEFAULT_FILTERS: Filters = {
  category: "",
  level: "",
  duration: 0,
  difficulty: "",
  instructor: "",
  instructorType: "all",
  date: "",
  location: "",
  price: [0, 5000],
  language: "",
  classLanguages: "",
  format: "",
  classType: "all",
  ageGroup: "all",
  instructorExperience: 0,
  rating: [0, 5],
  timeOfDay: "",
  specialFeatures: [],
  capacity: 0,
  accessibility: [],
  medicalClearanceRequired: false,
  parentalConsentRequired: false,
  equipmentProvided: [],
  waitlistAvailable: false,
  status: "published", // Only show published classes by default
};

// Sort option type
export type SortOption =
  | "newest"
  | "price-low"
  | "price-high"
  | "rating"
  | "closest-date";

export const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "newest", label: "Newest First" },
  { value: "price-low", label: "Price: Low → High" },
  { value: "price-high", label: "Price: High → Low" },
  { value: "rating", label: "Highest Rated" },
  { value: "closest-date", label: "Closest Date" },
];

// Keys we sync to URL (skip defaults/empty to keep URLs clean)
const URL_FILTER_KEYS = [
  "category",
  "instructor",
  "instructorType",
  "difficulty",
  "classType",
  "ageGroup",
  "format",
  "duration",
  "timeOfDay",
] as const;

/**
 * Hook to manage class filters, search term and sort option
 * Syncs state bidirectionally with URL search-params so filtered
 * views are shareable and bookmarkable.
 */
export function useClassFilters() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();

  // Rehydrate state from URL on mount
  const [filters, setFilters] = useState<Filters>(() => {
    const initial = { ...DEFAULT_FILTERS };
    const instructorId = searchParams.get("instructor");
    const category = searchParams.get("category");
    const instructorType = searchParams.get("instructorType");
    const difficulty = searchParams.get("difficulty");
    const classType = searchParams.get("classType");
    const ageGroup = searchParams.get("ageGroup");
    const format = searchParams.get("format");
    const duration = searchParams.get("duration");
    const timeOfDay = searchParams.get("timeOfDay");

    if (instructorId) initial.instructor = instructorId;
    if (category) initial.category = category;
    if (instructorType)
      initial.instructorType = instructorType as "all" | InstructorType;
    if (difficulty) initial.difficulty = difficulty;
    if (classType) initial.classType = classType as ClassType | "all";
    if (ageGroup) initial.ageGroup = ageGroup as AgeBand | "all";
    if (format) initial.format = format;
    if (duration) initial.duration = parseInt(duration) || 0;
    if (timeOfDay) initial.timeOfDay = timeOfDay;

    return initial;
  });

  const [searchTerm, setSearchTerm] = useState(
    () => searchParams.get("search") || ""
  );

  const [sortBy, setSortBy] = useState<SortOption>(
    () => (searchParams.get("sort") as SortOption) || "newest"
  );

  // Push current filter/search/sort state into URL (replaceState, no navigation)
  const syncToUrl = useCallback(
    (
      currentFilters: Filters,
      currentSearch: string,
      currentSort: SortOption
    ) => {
      const params = new URLSearchParams();

      // Search term
      if (currentSearch.trim()) {
        params.set("search", currentSearch.trim());
      }

      // Sort (only if not default)
      if (currentSort !== "newest") {
        params.set("sort", currentSort);
      }

      // Filters — only set non-default values
      for (const key of URL_FILTER_KEYS) {
        const value = currentFilters[key];
        const defaultValue = DEFAULT_FILTERS[key];

        if (
          value !== defaultValue &&
          value !== "" &&
          value !== null &&
          value !== undefined &&
          value !== 0
        ) {
          if (key === "instructorType" && value === "all") continue;
          if (key === "classType" && value === "all") continue;
          if (key === "ageGroup" && value === "all") continue;
          params.set(key, String(value));
        }
      }

      const qs = params.toString();
      const newUrl = qs ? `${pathname}?${qs}` : pathname;

      router.replace(newUrl, { scroll: false });
    },
    [pathname, router]
  );

  // Sync to URL when filters, search, or sort change
  useEffect(() => {
    // Debounce the URL sync slightly to avoid excessive pushes during rapid typing
    const timer = setTimeout(() => {
      syncToUrl(filters, searchTerm, sortBy);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters, searchTerm, sortBy, syncToUrl]);

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchTerm("");
    setSortBy("newest");
  };

  return {
    filters,
    setFilters,
    searchTerm,
    setSearchTerm,
    sortBy,
    setSortBy,
    resetFilters,
  };
}
